import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { debounce } from 'lodash';
import CouponPreview from './CouponPreviewStamp';
import ConfirmationDialog from './ConfirmationDialog';
import { FaTrash } from 'react-icons/fa';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 5000,
});

function RewardSetupPage() {
  const [stampSettings, setStampSettings] = useState({
    enabled: false,
    cooldownPeriod: '',
    maxStampsPerDay: '',
  });
  const [originalStampSettings, setOriginalStampSettings] = useState({});

  const [stampRewards, setStampRewards] = useState([]);
  const [newReward, setNewReward] = useState({
    stampCost: '',
    description: '',
    expirationDays: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState(null);

  const { restaurantId } = useParams();

  const fetchStampSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      const response = await api.get(
        `/restaurants/${restaurantId}/stamp-settings`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStampSettings(response.data);
      setOriginalStampSettings(response.data);
    } catch (error) {
      console.error('Error fetching stamp settings:', error);
      toast.error('Failed to fetch stamp settings. Please try again.');
    }
  }, [restaurantId]);

  const fetchStampRewards = useCallback(async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      const response = await api.get(
        `/restaurants/${restaurantId}/stamp-rewards`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStampRewards(response.data.stampRewards);
    } catch (error) {
      console.error('Error fetching stamp rewards:', error);
      toast.error('Failed to fetch stamp rewards. Please try again.');
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchStampSettings();
    fetchStampRewards();
  }, [fetchStampSettings, fetchStampRewards]);

  const handleInputChange = useCallback((field, value) => {
    setNewReward((prev) => ({ ...prev, [field]: value }));
  }, []);

  // const handleSettingsChange = (field, value) => {
  //   setStampSettings((prev) => ({ ...prev, [field]: value }));
  // };
  const handleSettingsChange = (field, value) => {
    setStampSettings((prev) => ({
      ...prev,
      [field]: value === '' ? null : value, // Set to null if the input is empty
    }));
  };

  //   const handleSettingsChange = async (field, value) => {
  //     try {
  //       const token = localStorage.getItem('ownerToken');

  //       await api.patch(
  //         `/restaurants/${restaurantId}/stamp-settings`,
  //         {
  //           [field]: value,
  //         },
  //         {
  //           headers: { Authorization: `Bearer ${token}` },
  //         }
  //       );
  //       setStampSettings((prev) => ({ ...prev, [field]: value }));
  //       toast.success('Stamp settings updated successfully');
  //     } catch (error) {
  //       console.error('Error updating stamp settings:', error);
  //       toast.error('Failed to update stamp settings');
  //     }
  //   };

  // const saveStampSettings = async () => {
  //   try {
  //     const token = localStorage.getItem('ownerToken');
  //     await api.patch(
  //       `/restaurants/${restaurantId}/stamp-settings`,
  //       stampSettings,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       }
  //     );
  //     setOriginalStampSettings(stampSettings);
  //     toast.success('Stamp settings updated successfully');
  //   } catch (error) {
  //     console.error('Error updating stamp settings:', error);
  //     toast.error('Failed to update stamp settings');
  //     setStampSettings(originalStampSettings); // Revert to original settings on error
  //   }
  // };

  const saveStampSettings = async () => {
    try {
      const token = localStorage.getItem('ownerToken');

      const payload = {
        ...stampSettings,
        cooldownPeriod:
          stampSettings.cooldownPeriod === '' ||
          stampSettings.cooldownPeriod === null
            ? null
            : parseInt(stampSettings.cooldownPeriod), // If empty, pass null
        maxStampsPerDay:
          stampSettings.maxStampsPerDay === '' ||
          stampSettings.maxStampsPerDay === null
            ? null
            : parseInt(stampSettings.maxStampsPerDay), // If empty, pass null
      };

      await api.patch(`/restaurants/${restaurantId}/stamp-settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOriginalStampSettings(stampSettings);
      toast.success('Stamp settings updated successfully');
    } catch (error) {
      console.error('Error updating stamp settings:', error);
      toast.error('Failed to update stamp settings');
      setStampSettings(originalStampSettings); // Revert to original settings on error
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('ownerToken');

      const response = await api.post(
        `/restaurants/${restaurantId}/stamp-rewards`,
        newReward,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStampRewards((prevRewards) => [response.data, ...prevRewards]);
      setNewReward({
        stampCost: 0,
        description: '',
        expirationDays: '',
      });
      toast.success('New stamp reward created successfully');
    } catch (error) {
      console.error('Error creating stamp reward:', error);
      setError(error.response?.data?.message || 'Error creating stamp reward');
      toast.error('Error creating stamp reward');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteReward = async () => {
    if (!rewardToDelete) return;

    try {
      const token = localStorage.getItem('ownerToken');

      await api.delete(
        `/restaurants/${restaurantId}/stamp-rewards/${rewardToDelete._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setStampRewards((prevRewards) =>
        prevRewards.filter((reward) => reward._id !== rewardToDelete._id)
      );
      toast.success('Stamp reward deleted successfully');
    } catch (error) {
      console.error('Error deleting stamp reward:', error);
      toast.error('Error deleting stamp reward');
    } finally {
      setIsDeleteModalOpen(false);
      setRewardToDelete(null);
    }
  };

  const RewardItem = React.memo(({ reward }) => (
    <li className='border p-4 rounded shadow-sm hover:shadow-md transition-shadow duration-200 flex justify-between items-center'>
      <div>
        <h3 className='font-bold text-lg'>{reward.description}</h3>
        <p className='text-gray-600'>Stamp Cost: {reward.stampCost}</p>
        <p className='text-gray-600'>
          Expiration Days: {reward.expirationDays || 'N/A'}
        </p>
      </div>
      <button
        onClick={() => {
          setRewardToDelete(reward);
          setIsDeleteModalOpen(true);
        }}
        className='text-red-500 hover:text-red-700 transition-colors duration-200'
      >
        <FaTrash className='h-5 w-5' />
      </button>
    </li>
  ));

  return (
    <div className='max-w-4xl mx-auto mt-10 px-4'>
      {/* <div className='flex justify-between items-center mb-6'> */}
      <div>
        <h1 className='text-3xl font-bold'>Stamp Reward Setup </h1>

        <h2 className='text-xl text-gray-600 mt-2 mb-2'>
          Stamp can only be collected using NFC scan. Please follow our
          guidelines for setting up the NFC scan service. If you have any
          troubles, we will help you so please contact ___
        </h2>
      </div>

      <div className='bg-white p-6 rounded-lg shadow-md mb-8'>
        <h2 className='text-2xl font-bold mb-4'>Stamp Settings</h2>
        <div className='space-y-4'>
          <div className='flex items-center'>
            <input
              type='checkbox'
              id='enabled'
              checked={stampSettings.enabled}
              onChange={(e) =>
                handleSettingsChange('enabled', e.target.checked)
              }
              className='h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded'
            />
            <label
              htmlFor='enabled'
              className='ml-2 block text-sm text-gray-900'
            >
              Enable Stamp System
            </label>
          </div>
          <div>
            <label
              htmlFor='maxStampsPerDay'
              className='block text-sm font-medium text-gray-700'
            >
              Max Stamps Per Day
            </label>
            <input
              type='number'
              id='maxStampsPerDay'
              value={stampSettings.maxStampsPerDay}
              onWheel={(e) => e.target.blur()}
              onChange={(e) =>
                handleSettingsChange(
                  'maxStampsPerDay',
                  parseInt(e.target.value)
                )
              }
              min='1'
              className='w-full p-2 border border-gray-300 rounded'
            />
          </div>
          <div>
            <label
              htmlFor='cooldownPeriod'
              className='block text-sm font-medium text-gray-700'
            >
              Cooldown Period (minutes)
            </label>
            <input
              type='number'
              id='cooldownPeriod'
              value={stampSettings.cooldownPeriod}
              onWheel={(e) => e.target.blur()}
              onChange={(e) =>
                handleSettingsChange('cooldownPeriod', parseInt(e.target.value))
              }
              min='1'
              className='w-full p-2 border border-gray-300 rounded'
            />
          </div>
        </div>
        <button
          onClick={saveStampSettings}
          className='mt-4 bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition-colors duration-200'
        >
          Save Settings
        </button>
      </div>

      <div className='flex flex-wrap gap-6 '>
        <form
          onSubmit={handleSubmit}
          className='bg-white p-6 rounded-lg shadow-md mb-8 flex-1'
        >
          <h2 className='text-2xl font-bold mb-4'>Create New Stamp Reward</h2>
          <div className='space-y-4'>
            <div>
              {/* <label
                htmlFor='stampCost'
                className='block text-sm font-medium text-gray-700'
              > */}
              <label className='block text-sm font-medium text-gray-700 text-nowrap'>
                Stamp Cost
              </label>
              <input
                type='number'
                id='stampCost'
                value={newReward.stampCost}
                onChange={(e) =>
                  handleInputChange('stampCost', parseInt(e.target.value))
                }
                required
                min='1'
                className='w-full p-2 border border-gray-300 rounded'
              />
            </div>
            <div>
              <label
                htmlFor='description'
                className='block text-sm font-medium text-gray-700'
              >
                Reward Description
              </label>
              <textarea
                id='description'
                value={newReward.description}
                onChange={(e) =>
                  handleInputChange('description', e.target.value)
                }
                required
                rows='3'
                className='w-full p-2 rounded border'
              ></textarea>
            </div>
            <div>
              {/* <label
                htmlFor='expirationDays'
                className='w-full p-2 border border-gray-300 rounded'
              > */}
              <label className='block text-sm font-medium text-gray-700 text-nowrap'>
                Expiration Days After Redeem (Optional)
              </label>
              <input
                type='number'
                id='expirationDays'
                value={newReward.expirationDays}
                onChange={(e) =>
                  handleInputChange('expirationDays', e.target.value)
                }
                min='0'
                onWheel={(e) => e.target.blur()}
                className='w-full p-2 border border-gray-300 rounded'
              />
            </div>
          </div>
          {error && <p className='text-red-500 mt-2'>{error}</p>}
          <button
            type='submit'
            className='mt-4 w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors duration-200'
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Stamp Reward'}
          </button>
        </form>

        <div className='bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6 rounded-lg shadow-md mb-8 flex-1'>
          {/* <h2 className='text-2xl font-bold mb-4'>Reward Preview</h2> */}
          <CouponPreview
            content={newReward.description}
            expirationDays={
              newReward.expirationDays ? newReward.expirationDays : null
            }
          />
        </div>
      </div>
      <div className='bg-white p-6 rounded-lg shadow-md'>
        <h2 className='text-2xl font-bold mb-4'>Existing Stamp Rewards</h2>
        <ul className='space-y-4'>
          {stampRewards.map((reward) => (
            <RewardItem key={reward._id} reward={reward} />
          ))}
        </ul>
      </div>

      <ConfirmationDialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteReward}
        title='Delete Stamp Reward'
        message='Are you sure you want to delete this stamp reward? This action cannot be undone.'
      />
    </div>
  );
}

export default React.memo(RewardSetupPage);
